import type { SignIn } from '@logto/schemas';
import { SignInIdentifier } from '@logto/schemas';
import { useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';

import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import useCheckSingleSignOn from '@/hooks/use-check-single-sign-on';
import useSendVerificationCode from '@/hooks/use-send-verification-code';
import { useSieMethods } from '@/hooks/use-sie';
import { UserFlow } from '@/types';
import { parsePhoneNumber } from '@/utils/country-code';
import { codeVerificationTypeMap } from '@/utils/sign-in-experience';

const useOnSubmit = (signInMethods: SignIn['methods']) => {
  const navigate = useNavigate();
  const { ssoConnectors } = useSieMethods();
  const { onSubmit: checkSingleSignOn } = useCheckSingleSignOn();
  const { setIdentifierInputValue, identifierInputValue, verificationIdsMap } =
    useContext(UserInteractionContext);

  const navigateToPasswordPage = useCallback(() => {
    navigate({
      pathname: `/${UserFlow.SignIn}/password`,
    });
  }, [navigate]);

  const {
    errorMessage,
    clearErrorMessage,
    onSubmit: sendVerificationCode,
  } = useSendVerificationCode(UserFlow.SignIn);

  const onSubmit = useCallback(
    async (identifier: SignInIdentifier, value: string) => {
      const method = signInMethods.find((method) => method.identifier === identifier);

      if (!method) {
        throw new Error(`Cannot find method with identifier type ${identifier}`);
      }

      setIdentifierInputValue({ type: identifier, value });

      const { password, isPasswordPrimary, verificationCode } = method;

      if (identifier === SignInIdentifier.Username) {
        navigateToPasswordPage();

        return;
      }

      // Check if the email is registered with any SSO connectors. If the email is registered with any SSO connectors, we should not proceed to the next step
      if (identifier === SignInIdentifier.Email && ssoConnectors.length > 0) {
        const result = await checkSingleSignOn(value);

        if (result) {
          return;
        }
      }

      if (password && (isPasswordPrimary || !verificationCode)) {
        navigateToPasswordPage();

        return;
      }

      if (verificationCode) {
        // If the user goes back and re-submits with the same identifier, skip sending
        // a new OTP and navigate directly to the verification code page.
        const existingVerificationId =
          verificationIdsMap[codeVerificationTypeMap[identifier as keyof typeof codeVerificationTypeMap]];

        // Normalize phone numbers to canonical countryCallingCode+nationalNumber
        // format (stripping trunk prefix leading zeros) so that e.g. "660812345678"
        // (stored when user typed local "0812345678") matches "66812345678" (emitted
        // after pre-fill, where libphonenumber strips the trunk prefix).
        const normalize =
          identifier === SignInIdentifier.Phone
            ? (v: string) => {
                const parsed = parsePhoneNumber(v);
                return parsed
                  ? `${parsed.countryCallingCode}${parsed.nationalNumber}`
                  : v.replace(/\D/g, '');
              }
            : (v: string) => v.trim().toLowerCase();

        const isSameIdentifier =
          identifierInputValue?.type === identifier &&
          normalize(identifierInputValue.value) === normalize(value);

        if (existingVerificationId && isSameIdentifier) {
          // Restore the original identifier value used when the OTP was sent.
          // Line 40 above already called setIdentifierInputValue with the current
          // form value (which may differ from the stored value, e.g. trunk-0 phone
          // "660931159127" vs pre-fill-normalized "66931159127"). Since batched
          // state updates take the last call, restoring here ensures the verify
          // call uses the exact same phone number the OTP was sent to.
          setIdentifierInputValue({ type: identifier, value: identifierInputValue.value });
          navigate({
            pathname: `/${UserFlow.SignIn}/verification-code`,
            search: window.location.search,
          });

          return;
        }

        await sendVerificationCode({ identifier, value });
      }
    },
    [
      signInMethods,
      setIdentifierInputValue,
      identifierInputValue,
      verificationIdsMap,
      ssoConnectors.length,
      navigateToPasswordPage,
      navigate,
      checkSingleSignOn,
      sendVerificationCode,
    ]
  );

  return {
    errorMessage,
    clearErrorMessage,
    onSubmit,
  };
};

export default useOnSubmit;
